import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtCompact, fmtCurrencyFull, fmtNumber, fmtDate } from "@/lib/format";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Truck, IndianRupee, Percent, Package, TrendingUp, TrendingDown, Wallet, FileText, Clock, Fuel, Receipt, Boxes, Coins } from "lucide-react";
import { Empty } from "@/components/PageIntro";

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#f43f5e", "#06b6d4", "#a855f7"];

const toneClasses = {
  primary: "text-primary bg-primary/10 border-primary/20",
  emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
  amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
  cyan: "text-cyan-500 bg-cyan-500/10 border-cyan-500/20",
  rose: "text-rose-500 bg-rose-500/10 border-rose-500/20",
};

const MiniStat = ({ icon: Icon, label, value, sub, tone = "primary", testid }) => (
  <div data-testid={testid} className="bg-card border border-border/60 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm hover:border-border transition-colors">
    <div className={`p-2 rounded-lg border shrink-0 ${toneClasses[tone]}`}><Icon className="h-4 w-4" /></div>
    <div className="min-w-0">
      <div className="text-lg font-bold font-mono leading-tight" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, tone = "primary", testid }) => {
  const tones = {
    primary: "text-primary bg-primary/10 border-primary/20",
    emerald: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    amber: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    cyan: "text-cyan-500 bg-cyan-500/10 border-cyan-500/20",
    rose: "text-rose-500 bg-rose-500/10 border-rose-500/20",
  };
  const strip = {
    primary: "from-primary to-sky-400",
    emerald: "from-emerald-500 to-teal-400",
    amber: "from-amber-500 to-orange-400",
    cyan: "from-cyan-500 to-sky-400",
    rose: "from-rose-500 to-pink-400",
  };
  return (
    <div
      data-testid={testid}
      className="bg-card border border-border/60 rounded-xl p-5 shadow-sm transition-all duration-200 hover:border-border hover:shadow-lg hover:-translate-y-0.5 group relative overflow-hidden"
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${strip[tone]} opacity-70 group-hover:opacity-100 transition-opacity`} />
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-lg border ${tones[tone]} transition-transform group-hover:scale-110`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight font-mono" data-testid={`${testid}-value`}>
        {value}
      </div>
      <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
};

const statusBadge = (s) => {
  const map = {
    "In Transit": "bg-amber-500/10 text-amber-500 border-amber-500/20",
    Delivered: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  };
  return `px-2.5 py-1 rounded-full text-xs font-semibold border ${map[s] || "bg-muted text-muted-foreground border-border"}`;
};

export default function Dashboard() {
  const { selectedHub, year, hubName } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard`, { params: { hub: selectedHub, year } }).then(({ data }) => {
      setData(data);
      setLoading(false);
    });
  }, [selectedHub, year]);

  if (loading || !data) return <div className="py-20 text-center text-muted-foreground">Loading dashboard…</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-trips" icon={Truck} label="Total Trips" value={fmtNumber(k.trips)} tone="primary" sub="This year" />
        <StatCard testid="kpi-revenue" icon={IndianRupee} label="Revenue" value={fmtCompact(k.revenue)} tone="emerald" sub={fmtCurrencyFull(k.revenue)} />
        <StatCard testid="kpi-commission" icon={Percent} label="Commission" value={fmtCompact(k.commission)} tone="amber" />
        <StatCard testid="kpi-quantity" icon={Package} label="Quantity (Tons)" value={fmtNumber(k.quantity)} tone="cyan" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-net-profit" icon={k.net_profit >= 0 ? TrendingUp : TrendingDown} label="Net Profit" value={fmtCompact(k.net_profit)} tone={k.net_profit >= 0 ? "emerald" : "rose"} sub={`Margin ${k.revenue ? Math.round((k.net_profit / k.revenue) * 100) : 0}%`} />
        <StatCard testid="kpi-expenses" icon={Wallet} label="Total Expenses" value={fmtCompact(k.expenses)} tone="rose" sub={fmtCurrencyFull(k.expenses)} />
        <StatCard testid="kpi-unbilled" icon={FileText} label="Unbilled Revenue" value={fmtCompact(k.unbilled_revenue)} tone="amber" sub={fmtCurrencyFull(k.unbilled_revenue)} />
        <StatCard testid="kpi-advances" icon={Coins} label="Advances Outstanding" value={fmtCompact(k.advances_outstanding)} tone="primary" sub={fmtCurrencyFull(k.advances_outstanding)} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat icon={Clock} label="Pending Acknowledgements" value={fmtNumber(k.pending_ack_count)} sub={fmtCurrencyFull(k.pending_ack_value)} tone="amber" testid="mini-pending-ack" />
        <MiniStat icon={Boxes} label="Active Trucks" value={fmtNumber(k.active_trucks)} sub={`${fmtNumber(k.own_trucks)} own · ${fmtNumber(k.market_trucks)} market`} tone="cyan" testid="mini-trucks" />
        <MiniStat icon={Fuel} label="Fuel Purchased" value={`${fmtNumber(k.fuel_litres)} L`} sub={`Diesel ${fmtCurrencyFull(k.diesel_spend)}`} tone="rose" testid="mini-fuel" />
        <MiniStat icon={Receipt} label="FASTag Toll Spend" value={fmtCompact(k.toll_spend)} sub={fmtCurrencyFull(k.toll_spend)} tone="primary" testid="mini-toll" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-earnings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Monthly Earnings by Hub</h3>
            <span className="text-xs text-muted-foreground">{year}</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.monthly_earning}>
              <defs>
                {data.hub_names.map((hn, i) => (
                  <linearGradient key={hn} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={60} />
              <Tooltip formatter={(v) => fmtCurrencyFull(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
              <Legend />
              {data.hub_names.map((hn, i) => (
                <Area key={hn} type="monotone" dataKey={hn} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`url(#g${i})`} strokeWidth={2} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-expense-category">
          <h3 className="font-display text-lg font-semibold mb-4">Expense Breakdown</h3>
          {data.expense_by_category.length === 0 ? (
            <Empty text="No expenses recorded" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.expense_by_category} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {data.expense_by_category.map((e, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtCurrencyFull(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-monthly-expense">
        <h3 className="font-display text-lg font-semibold mb-4">Monthly Expenses</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.monthly_expense}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
            <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={60} />
            <Tooltip formatter={(v) => fmtCurrencyFull(v)} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
            <Bar dataKey="amount" fill="#f43f5e" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Operational insight charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-top-owners">
          <h3 className="font-display text-lg font-semibold mb-4">Top Owners by Freight</h3>
          {(!data.top_owners || data.top_owners.length === 0) ? <Empty text="No trips yet" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart layout="vertical" data={data.top_owners} margin={{ left: 8, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                <YAxis type="category" dataKey="name" width={110} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtCurrencyFull(v)} cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Bar dataKey="freight" fill="#3b82f6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-fleet">
          <h3 className="font-display text-lg font-semibold mb-4">Fleet: Own vs Market</h3>
          {k.active_trucks === 0 ? <Empty text="No trucks added" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.ownership_split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {data.ownership_split.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-status">
          <h3 className="font-display text-lg font-semibold mb-4">Challan Status</h3>
          {k.trips === 0 ? <Empty text="No trips yet" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.status_split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {data.status_split.map((e, i) => <Cell key={i} fill={["#10b981", "#f59e0b"][i % 2]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Material load trend */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="chart-material-load">
        <h3 className="font-display text-lg font-semibold mb-4">Material Load Trend (Tons)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.material_load_trend || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
            <YAxis stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} tickFormatter={(v) => fmtNumber(v)} width={60} />
            <Tooltip formatter={(v) => `${fmtNumber(v)} Tons`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
            <Legend />
            {(data.materials || []).map((m, i) => (
              <Line key={m} type="monotone" dataKey={m} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent trips */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden" data-testid="recent-trips">
        <div className="p-5 border-b border-border">
          <h3 className="font-display text-lg font-semibold">Recent Trips</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase font-bold text-muted-foreground tracking-wider">
              <tr>
                <th className="py-3 px-4 text-left">Trip #</th>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Hub</th>
                <th className="py-3 px-4 text-left">Material</th>
                <th className="py-3 px-4 text-right">Qty (T)</th>
                <th className="py-3 px-4 text-right">Freight</th>
                <th className="py-3 px-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_trips.map((t) => (
                <tr key={t.id} className="border-t border-border/60 hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4 font-mono font-medium">{t.trip_number}</td>
                  <td className="py-3 px-4 text-muted-foreground">{fmtDate(t.trip_date)}</td>
                  <td className="py-3 px-4">{hubName(t.origin_hub_id)}</td>
                  <td className="py-3 px-4">{t.goods_type}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtNumber(t.quantity)}</td>
                  <td className="py-3 px-4 text-right font-mono">{fmtCurrencyFull(t.freight_amount)}</td>
                  <td className="py-3 px-4 text-center"><span className={statusBadge(t.status)}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
